<template>
  <div class="bg-n-slate-2 dark:bg-n-slate-3 rounded-xl shadow-sm border border-n-weak p-6 flex flex-col" :style="{ minHeight: chartHeight }">
    <h3 class="text-lg font-semibold text-n-slate-12 mb-6">
      📈 Distribuição de Conversas por Agente
    </h3>
    <div v-if="data && data.length > 0" class="relative flex-1 min-h-0">
      <canvas ref="chartCanvas"></canvas>
    </div>
    <div v-else class="text-center py-8">
      <p class="text-n-slate-10">
        Nenhuma conversa distribuída no período
      </p>
    </div>
  </div>
</template>

<script>
import { ref, watch, onMounted, onUnmounted, computed, nextTick } from 'vue';
import { Chart, registerables } from 'chart.js';

Chart.register(...registerables);

export default {
  name: 'DistributionChart',
  props: {
    data: {
      type: Array,
      default: () => [],
    },
    selectedAgentId: {
      type: Number,
      default: null,
    },
  },
  emits: ['agent-click'],
  setup(props, { emit }) {
    const chartCanvas = ref(null);
    let chartInstance = null;

    const isDarkMode = computed(() => {
      if (typeof window === 'undefined') return false;
      return document.documentElement.classList.contains('dark');
    });

    const chartHeight = computed(() => {
      const agentCount = props.data?.length || 0;
      if (agentCount === 0) return '400px';
      
      // 50px por agente + 200px de padding/header
      const calculatedHeight = agentCount * 50 + 200;
      const finalHeight = Math.min(Math.max(calculatedHeight, 400), 1200);
      
      return `${finalHeight}px`;
    });

    const getColorFromCSSVar = (varName) => {
      if (typeof window === 'undefined') return '#3b82f6';
      const root = document.documentElement;
      const value = getComputedStyle(root).getPropertyValue(varName).trim();
      return value ? `rgb(${value})` : '#3b82f6';
    };

    const colors = [
      getColorFromCSSVar('--blue-9'),
      getColorFromCSSVar('--iris-9'),
      getColorFromCSSVar('--teal-9'),
      getColorFromCSSVar('--amber-9'),
      getColorFromCSSVar('--ruby-9'),
      getColorFromCSSVar('--blue-11'),
      getColorFromCSSVar('--iris-11'),
      getColorFromCSSVar('--teal-11'),
      getColorFromCSSVar('--amber-11'),
      getColorFromCSSVar('--ruby-11'),
    ];

    const getColor = (index) => {
      return colors[index % colors.length];
    };

    const createChart = async () => {
      await nextTick();
      
      if (!chartCanvas.value) {
        console.warn('⚠️ DistributionChart - Canvas not found');
        return;
      }

      if (!props.data || props.data.length === 0) {
        console.warn('⚠️ DistributionChart - No data to display');
        return;
      }

      if (chartInstance) {
        chartInstance.destroy();
      }

      const sortedData = [...props.data].sort((a, b) => b.count - a.count);
      const ctx = chartCanvas.value.getContext('2d');

      const getBackgroundColors = () => {
        return sortedData.map((item, index) => {
          const baseColor = getColor(index);

          if (!props.selectedAgentId) return baseColor;
          if (item.user_id === props.selectedAgentId) return baseColor;

          const rgb = baseColor.match(/\w\w/g).map(x => parseInt(x, 16));
          return `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, 0.3)`;
        });
      };

      const getBorderWidths = () => {
        return sortedData.map(item => {
          if (!props.selectedAgentId) return 0;
          return item.user_id === props.selectedAgentId ? 3 : 0;
        });
      };

      const gridColor = isDarkMode.value 
        ? 'rgba(255, 255, 255, 0.08)' 
        : 'rgba(0, 0, 0, 0.05)';

      const textColor = isDarkMode.value 
        ? 'rgb(203, 213, 225)'
        : 'rgb(71, 85, 105)';

      const tooltipBg = isDarkMode.value 
        ? 'rgba(15, 23, 42, 0.95)' 
        : 'rgba(255, 255, 255, 0.95)';

      const tooltipBorder = isDarkMode.value 
        ? 'rgba(71, 85, 105, 0.5)' 
        : 'rgba(203, 213, 225, 0.5)';

      chartInstance = new Chart(ctx, {
        type: 'bar',
        data: {
          labels: sortedData.map(item => item.name),
          datasets: [
            {
              label: 'Conversas',
              data: sortedData.map(item => item.count),
              backgroundColor: getBackgroundColors(),
              borderColor: sortedData.map((item, index) => getColor(index)),
              borderWidth: getBorderWidths(),
              borderRadius: 8,
              barPercentage: 0.7,
              categoryPercentage: 0.8,
            },
          ],
        },
        options: {
          indexAxis: 'y',
          responsive: true,
          maintainAspectRatio: false,
          layout: {
            padding: {
              bottom: 10,
            },
          },
          onClick: (event, elements) => {
            if (elements.length > 0) {
              const index = elements[0].index;
              const agentData = sortedData[index];
              emit('agent-click', agentData.user_id);
            }
          },
          plugins: {
            legend: {
              display: false,
            },
            tooltip: {
              backgroundColor: tooltipBg,
              titleColor: textColor,
              bodyColor: textColor,
              borderColor: tooltipBorder,
              borderWidth: 1,
              padding: 12,
              displayColors: true,
              callbacks: {
                label: function (context) {
                  const value = context.parsed.x || 0;
                  const percentage = sortedData[context.dataIndex]?.percentage || 0;
                  return `Conversas: ${value} (${percentage}%)`;
                },
              },
            },
          },
          scales: {
            x: {
              position: 'bottom',
              beginAtZero: true,
              ticks: {
                color: textColor,
                precision: 0,
                padding: 5,
              },
              grid: {
                color: gridColor,
                drawBorder: true,
                drawOnChartArea: true,
                borderColor: gridColor,
                borderWidth: 2,
              },
            },
            y: {
              ticks: {
                color: textColor,
                font: {
                  size: 12,
                },
                callback: function(value, index) {
                  const label = this.getLabelForValue(value);
                  if (label.length > 20) {
                    return label.substring(0, 17) + '...';
                  }
                  return label;
                },
              },
              grid: {
                display: false,
              },
              border: {
                color: gridColor,
              },
            },
          },
        },
      });
    };

    const observer = new MutationObserver(() => {
      createChart();
    });

    watch(() => props.data, () => {
      createChart();
    }, { deep: true });

    watch(() => props.selectedAgentId, () => {
      createChart();
    });

    onMounted(() => {
      createChart();
      
      if (typeof window !== 'undefined') {
        observer.observe(document.documentElement, {
          attributes: true,
          attributeFilter: ['class'],
        });
      }
    });

    onUnmounted(() => {
      if (chartInstance) {
        chartInstance.destroy();
      }
      observer.disconnect();
    });

    return {
      chartCanvas,
      chartHeight,
      getColor,
    };
  },
};
</script>